import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Skeleton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Divider
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  ReferenceLine
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import {
  getJiraBoards,
  getBoardVelocity,
  getSprintComparison
} from '../../utils/api';

const SprintComparison = ({ scope, organizationId, teamId }) => {
  // Note: Sprint comparison uses JIRA board data which is not filtered by workspace
  // The props are accepted for interface consistency but not used in API calls
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState('');
  const [comparisonDate, setComparisonDate] = useState('');
  const [velocityData, setVelocityData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [boardsLoading, setBoardsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBoards();
  }, []);

  const fetchBoards = async () => {
    setBoardsLoading(true);
    try {
      const response = await getJiraBoards();
      setBoards(response.values || []);
    } catch (err) {
      console.error('Error fetching boards:', err);
      setError('Failed to load JIRA boards. Please check your JIRA configuration in Settings.');
    } finally {
      setBoardsLoading(false);
    }
  };

  const fetchVelocityData = async (boardId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getBoardVelocity(boardId, 12);
      setVelocityData(response);
    } catch (err) {
      console.error('Error fetching velocity:', err);
      setError('Failed to load velocity data');
    } finally {
      setLoading(false);
    }
  };

  const fetchComparison = async () => {
    if (!selectedBoard || !comparisonDate) return;

    setLoading(true);
    setError(null);
    try {
      const response = await getSprintComparison(selectedBoard, comparisonDate, 20);
      setComparisonData(response);
    } catch (err) {
      console.error('Error fetching comparison:', err);
      setError('Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  };

  const handleBoardChange = (event) => {
    const boardId = event.target.value;
    setSelectedBoard(boardId);
    setComparisonData(null);
    if (boardId) {
      fetchVelocityData(boardId);
    }
  };

  const getTrendIcon = (value) => {
    if (value > 5) return <TrendingUpIcon color="success" />;
    if (value < -5) return <TrendingDownIcon color="error" />;
    return <TrendingFlatIcon color="action" />;
  };

  const getAssessmentColor = (assessment) => {
    switch (assessment) {
      case 'significant_improvement': return 'success';
      case 'moderate_improvement': return 'info';
      default: return 'warning';
    }
  };

  const getAssessmentLabel = (assessment) => {
    switch (assessment) {
      case 'significant_improvement': return 'Significant Improvement';
      case 'moderate_improvement': return 'Moderate Improvement';
      default: return 'No Significant Change';
    }
  };

  const MetricCard = ({ title, before, after, unit = '', improvement, improvedIsGood = true }) => {
    const isImproved = improvedIsGood ? improvement > 0 : improvement < 0;
    const displayImprovement = improvedIsGood ? improvement : -improvement;

    return (
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Before</Typography>
              <Typography variant="h6">{before}{unit}</Typography>
            </Box>
            <CompareArrowsIcon color="action" />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">After</Typography>
              <Typography variant="h6" color={isImproved ? 'success.main' : 'error.main'}>
                {after}{unit}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
            {getTrendIcon(displayImprovement)}
            <Typography
              variant="body2"
              color={isImproved ? 'success.main' : 'error.main'}
              sx={{ ml: 0.5 }}
            >
              {displayImprovement > 0 ? '+' : ''}{displayImprovement}{unit === '%' ? ' pts' : '%'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const VelocityChart = ({ data }) => {
    if (!data || !data.sprints || data.sprints.length === 0) {
      return <Alert severity="info">No sprint data available</Alert>;
    }

    const chartData = data.sprints.map(sprint => ({
      name: sprint.sprintName.replace(/Sprint\s*/i, 'S'),
      committed: sprint.committed,
      completed: sprint.completed,
      completionRate: sprint.completionRate
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
            tick={{ fontSize: 11 }}
          />
          <YAxis />
          <RechartsTooltip
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                return (
                  <Paper sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2">{label}</Typography>
                    <Typography variant="body2" color="primary">
                      Committed: {payload[0]?.value} pts
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      Completed: {payload[1]?.value} pts
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completion: {payload[0]?.payload?.completionRate}%
                    </Typography>
                  </Paper>
                );
              }
              return null;
            }}
          />
          <Legend />
          <Bar dataKey="committed" fill="#90CAF9" name="Committed" />
          <Bar dataKey="completed" fill="#4CAF50" name="Completed" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const ComparisonChart = ({ data }) => {
    if (!data) return null;

    const allSprints = [
      ...data.before.sprints.map(s => ({ ...s, period: 'before' })),
      ...data.after.sprints.map(s => ({ ...s, period: 'after' }))
    ].sort((a, b) => new Date(a.endDate) - new Date(b.endDate));

    const chartData = allSprints.map(sprint => ({
      name: sprint.name.replace(/Sprint\s*/i, 'S'),
      velocity: sprint.velocity,
      completionRate: sprint.completionRate,
      period: sprint.period
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
            tick={{ fontSize: 11 }}
          />
          <YAxis yAxisId="left" />
          <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
          <RechartsTooltip />
          <Legend />
          <ReferenceLine
            x={chartData.findIndex(d => d.period === 'after') > 0
              ? chartData[chartData.findIndex(d => d.period === 'after')]?.name
              : null}
            stroke="#FF9800"
            strokeDasharray="5 5"
            label={{ value: 'Started Planning Poker', fill: '#FF9800', fontSize: 10 }}
            yAxisId="left"
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="velocity"
            stroke="#2196F3"
            strokeWidth={2}
            dot={{ fill: '#2196F3' }}
            name="Velocity (pts)"
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="completionRate"
            stroke="#4CAF50"
            strokeWidth={2}
            dot={{ fill: '#4CAF50' }}
            name="Completion Rate (%)"
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  if (boardsLoading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Sprint Comparison</Typography>
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Sprint Comparison - Before & After Planning Poker
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Compare your team's sprint performance before and after adopting structured estimation.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>JIRA Board</InputLabel>
              <Select
                value={selectedBoard}
                onChange={handleBoardChange}
                label="JIRA Board"
              >
                {boards.map((board) => (
                  <MenuItem key={board.id} value={board.id}>
                    {board.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Started Using Planning Poker"
              value={comparisonDate}
              onChange={(e) => setComparisonDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Select when your team started using this tool"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              onClick={fetchComparison}
              disabled={!selectedBoard || !comparisonDate || loading}
              fullWidth
            >
              Compare Sprints
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading && (
        <Box sx={{ mt: 2 }}>
          <Skeleton variant="rectangular" height={300} sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map(i => (
              <Grid item xs={6} md={3} key={i}>
                <Skeleton variant="rectangular" height={120} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {!loading && velocityData && !comparisonData && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Recent Sprint Velocity
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Chip
              label={`Avg Velocity: ${velocityData.summary?.avgVelocity || 0} pts`}
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`Avg Completion: ${velocityData.summary?.avgCompletionRate || 0}%`}
              color="success"
              variant="outlined"
            />
            <Chip
              icon={getTrendIcon(velocityData.summary?.velocityTrend || 0)}
              label={`Trend: ${velocityData.summary?.trendDirection || 'stable'}`}
              variant="outlined"
            />
          </Box>
          <VelocityChart data={velocityData} />
        </Paper>
      )}

      {!loading && comparisonData && (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Performance Comparison
              </Typography>
              <Chip
                label={getAssessmentLabel(comparisonData.summary?.overallAssessment)}
                color={getAssessmentColor(comparisonData.summary?.overallAssessment)}
              />
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <MetricCard
                  title="Average Velocity"
                  before={comparisonData.before.avgVelocity}
                  after={comparisonData.after.avgVelocity}
                  unit=" pts"
                  improvement={comparisonData.improvements.velocityChange}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <MetricCard
                  title="Completion Rate"
                  before={comparisonData.before.avgCompletionRate}
                  after={comparisonData.after.avgCompletionRate}
                  unit="%"
                  improvement={comparisonData.improvements.completionRateChange}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <MetricCard
                  title="Estimation Coverage"
                  before={comparisonData.before.avgEstimationCoverage}
                  after={comparisonData.after.avgEstimationCoverage}
                  unit="%"
                  improvement={comparisonData.improvements.estimationCoverageChange}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <MetricCard
                  title="Sprints Analyzed"
                  before={comparisonData.before.sprintCount}
                  after={comparisonData.after.sprintCount}
                  improvement={0}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Velocity & Completion Trend
            </Typography>
            <ComparisonChart data={comparisonData} />
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Before Planning Poker ({comparisonData.before.sprintCount} sprints)
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Total Committed: <strong>{comparisonData.before.totalCommitted} pts</strong>
                  </Typography>
                  <Typography variant="body2">
                    Total Completed: <strong>{comparisonData.before.totalCompleted} pts</strong>
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  After Planning Poker ({comparisonData.after.sprintCount} sprints)
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Total Committed: <strong>{comparisonData.after.totalCommitted} pts</strong>
                  </Typography>
                  <Typography variant="body2">
                    Total Completed: <strong>{comparisonData.after.totalCompleted} pts</strong>
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {!loading && !velocityData && !comparisonData && boards.length > 0 && (
        <Alert severity="info">
          Select a JIRA board to view sprint velocity data, then choose a comparison date to see before/after metrics.
        </Alert>
      )}

      {!loading && boards.length === 0 && !error && (
        <Alert severity="warning">
          No JIRA boards found. Please configure your JIRA integration in Settings first.
        </Alert>
      )}
    </Box>
  );
};

export default SprintComparison;
